import {getLocalizationBasedOnLang} from '../../../../data/models/User/UserInfo';

/*
 * The Studio's videos page: the microreels a job generated, playing on a phone, with sharing
 * beside them. "Microreel" is the platform's own word and "Microreels" its page's name, so the
 * Welsh keeps both; Welsh counts take the singular noun.
 */

const english = {
  loading: 'Loading…',

  // The page head: renaming, what the job holds, and the actions on the right.
  renameBlocked: 'Wait for the current Studio operation to finish.',
  reelCount: (count: number) =>
    `${count} ${count === 1 ? 'microreel' : 'microreels'}`,
  inTotal: (duration: string) => `${duration} in total`,
  storyboardLabel: 'Storyboard',
  preview: 'Preview',
  previewUnavailable: 'Available when the microreel is ready',
  edit: 'Edit',
  download: 'Download',

  // Sharing, in the page head and in the card under the microreel list.
  addToCourse: 'Add to course',
  shareWithLearners: 'Share with learners',
  sharing: 'Sharing…',
  opening: 'Opening…',
  getInFrontTitle: 'Get them in front of learners',
  getInFrontHint:
    'Send them out directly, or fold them into an existing course.',
  // Why the share buttons are disabled.
  hintGenerating: (count: number): string =>
    count === 1
      ? 'Available once the microreel finishes generating.'
      : 'Available once all microreels finish generating.',
  hintNotGenerated: (count: number): string =>
    count === 1
      ? 'Generate the microreel before sharing.'
      : 'Generate the microreels before sharing.',
  hintSaving: (count: number): string =>
    count === 1
      ? 'Available once the microreel finishes saving.'
      : 'Available once the microreels finish saving.',
  hintFixKnowledgeCheck: 'Fix the knowledge check before sharing.',
  hintFixAttestation: 'Fix the policy attestation before sharing.',
  hintCreditCheck:
    'Available once the AI credits from your last generation are settled.',
  hintChecking: (count: number): string =>
    count === 1
      ? 'Checking whether this microreel is already shared…'
      : 'Checking whether these microreels are already shared…',
  beforeSharing: 'Before sharing',
  fixOnStoryboard: 'Fix it on the storyboard',
  noNewRender: (count: number): string =>
    count === 1
      ? 'the microreel does not need to be generated again.'
      : 'the microreels do not need to be generated again.',
  shared: 'Shared · learners get this version',
  changedSinceShared:
    'Changed since you last shared. Sharing sends this version to learners.',
  notShared: (count: number): string =>
    count === 1
      ? 'Not shared yet. It is in your Microreels; sharing sends it to learners.'
      : 'Not shared yet. They are in your Microreels; sharing sends them to learners.',
  shareFailed: 'The microreels could not be shared. Try again in a moment.',
  learnerListNotReady:
    'The microreels are shared, but their learner list is not ready yet. Try again in a moment.',

  // Alerts above the player.
  refresh: 'Refresh',
  reloadMicroreels: 'Reload microreels',
  generationStopped: (ready: number, count: number) =>
    `Generation stopped after ${ready} of ${count} microreels.`,
  // A render that made every microreel stopped before it finished their files.
  stoppedBeforeFinishing: (count: number): string =>
    count === 1
      ? 'Generation stopped before your microreel was finished.'
      : 'Generation stopped before your microreels were finished.',
  tryAgain: 'Try again',

  // The phone and what plays on it.
  phoneLabel: (title: string, number: number, count: number) =>
    `${title} · microreel ${number}/${count}`,
  watchPlayablePreview: 'Watch playable preview',
  watchFinishedVideo: 'Watch the finished video',
  previewReady: 'Preview ready to play.',
  loadingPreview: 'Loading preview…',
  previewFrameTitle: (title: string, reelTitle: string) =>
    `${title}: ${reelTitle} playable preview`,
  previewSlow: 'Preview taking too long?',
  openPreviewInTab: 'Open playable preview in a new tab',
  learnerPreview: 'Learner preview',
  exitPreview: 'Exit preview',
  sceneMarker: (number: number) => `Scene ${number}`,
  clipMarker: (number: number) => `Clip ${number}`,

  // What the phone shows while the microreel on it cannot play.
  makingMicroreels: 'Making your microreels',
  renderingScenes:
    'Creating narration and visuals. Each one plays here when it is ready.',
  renderingClips:
    'Cutting the clips from your video. Each one plays here when it is ready.',
  renderSteps: [
    'Preparing your preview',
    'Exporting the microreels',
    'Finishing your files',
  ],
  // Clipping cuts each microreel from the video; the cut microreel is its own preview.
  clipRenderSteps: [
    'Cutting the clips from your video',
    'Exporting the microreels',
    'Finishing your files',
  ],
  // The SI symbol for seconds, as the render counts up.
  elapsed: (seconds: string) => `${seconds}s`,
  watchMicroreel: (number: number) => `Watch microreel ${number}`,
  notGenerated: (count: number): string =>
    count === 1
      ? 'The microreel could not be generated'
      : 'The microreels could not be generated',
  // A render that stopped part way, on a microreel it did not make: one the failure names, or
  // one it may never have reached.
  reelNotGenerated: 'This microreel could not be generated',
  reelNotGeneratedYet: 'This microreel is not generated yet',
  stillDrafting: 'Your storyboard is still being drafted',
  openStoryboard: 'Open the storyboard',
  storyboardChanged: 'The storyboard changed',
  // A new look or sound from this page: generating again shows it and reuses the rest.
  newLookReady: 'A new look is ready to preview',
  newLookHint:
    'Regenerate to see it. The narration, voice and pictures are reused.',
  regenerateScenes:
    'Regenerate to see your changes. Unchanged scenes are reused.',
  regenerateClips:
    'Regenerate to see your changes. Unchanged clips are reused.',
  regenerate: 'Regenerate',
  noneYet: (count: number): string =>
    count === 1 ? 'No microreel yet' : 'No microreels yet',
  reviewThenGenerate:
    'Review the storyboard, then generate. This microreel has not been rendered yet.',
  reviewStoryboard: 'Review the storyboard',

  // The microreel list beside the phone.
  listLabel: 'Microreels',
  changedAfterMade: 'The storyboard changed after these microreels were made.',
  selectToPlay: 'Select one to play it',
  chipPreviewReady: 'Preview ready',
  chipUpNext: 'Up next',
  chipGenerating: 'Generating…',
  chipNotGenerated: 'Not generated',
  chipChanged: 'Changed since last generated',
  scenes: (count: number) => `${count} ${count === 1 ? 'scene' : 'scenes'}`,
  clips: (count: number) => `${count} ${count === 1 ? 'clip' : 'clips'}`,
  editStoryboard: 'Edit storyboard',
  editStoryboardOf: (title: string) => `Edit the storyboard of ${title}`,
  editKnowledgeCheck: 'Edit the knowledge check',
  editKnowledgeCheckOf: (title: string) =>
    `Edit the knowledge check of ${title}`,
  editAttestation: 'Edit the policy attestation',
  editAttestationOf: (title: string) =>
    `Edit the policy attestation of ${title}`,
  downloadVideo: 'Download video',
  downloadReel: (title: string) => `Download ${title}`,

  // Under the player.
  brandingSummary: 'Video settings & branding · all microreels',
  lookSummary: (title: string) => `Look & sound · ${title}`,
  sourceReferences: 'Source references',
};

const welsh: typeof english = {
  loading: 'Yn llwytho…',

  renameBlocked: 'Arhoswch i’r gwaith presennol yn Studio orffen.',
  reelCount: (count: number) => `${count} microreel`,
  inTotal: (duration: string) => `${duration} i gyd`,
  storyboardLabel: 'Bwrdd stori',
  preview: 'Rhagolwg',
  previewUnavailable: 'Ar gael pan fydd y microreel yn barod',
  edit: 'Golygu',
  download: 'Llwytho i lawr',

  addToCourse: 'Ychwanegu at gwrs',
  shareWithLearners: 'Rhannu â dysgwyr',
  sharing: 'Yn rhannu…',
  opening: 'Yn agor…',
  getInFrontTitle: 'Rhowch nhw o flaen dysgwyr',
  getInFrontHint:
    'Anfonwch nhw’n uniongyrchol, neu eu cynnwys mewn cwrs sy’n bodoli eisoes.',
  hintGenerating: (count: number) =>
    count === 1
      ? 'Ar gael pan fydd y microreel wedi gorffen cynhyrchu.'
      : 'Ar gael pan fydd pob microreel wedi gorffen cynhyrchu.',
  hintNotGenerated: (count: number) =>
    count === 1
      ? 'Cynhyrchwch y microreel cyn ei rannu.'
      : 'Cynhyrchwch y microreels cyn eu rhannu.',
  hintSaving: (count: number) =>
    count === 1
      ? 'Ar gael pan fydd y microreel wedi gorffen cadw.'
      : 'Ar gael pan fydd y microreels wedi gorffen cadw.',
  hintFixKnowledgeCheck: 'Trwsiwch y gwiriad gwybodaeth cyn rhannu.',
  hintFixAttestation: 'Trwsiwch y cadarnhad polisi cyn rhannu.',
  hintCreditCheck:
    'Ar gael pan fydd credydau AI eich cynhyrchiad diwethaf wedi’u setlo.',
  hintChecking: (count: number) =>
    count === 1
      ? 'Yn gwirio a yw’r microreel hwn wedi’i rannu eisoes…'
      : 'Yn gwirio a yw’r microreels hyn wedi’u rhannu eisoes…',
  beforeSharing: 'Cyn rhannu',
  fixOnStoryboard: 'Trwsiwch hyn ar y bwrdd stori',
  noNewRender: (count: number) =>
    count === 1
      ? 'nid oes angen cynhyrchu’r microreel eto.'
      : 'nid oes angen cynhyrchu’r microreels eto.',
  shared: 'Wedi’i rannu · mae dysgwyr yn cael y fersiwn hon',
  changedSinceShared:
    'Wedi newid ers i chi rannu ddiwethaf. Bydd rhannu yn anfon y fersiwn hon at ddysgwyr.',
  notShared: (count: number) =>
    count === 1
      ? 'Heb ei rannu eto. Mae yn eich Microreels; bydd rhannu yn ei anfon at ddysgwyr.'
      : 'Heb eu rhannu eto. Maen nhw yn eich Microreels; bydd rhannu yn eu hanfon at ddysgwyr.',
  shareFailed:
    'Nid oedd modd rhannu’r microreels. Rhowch gynnig arall arni mewn munud.',
  learnerListNotReady:
    'Mae’r microreels wedi’u rhannu, ond nid yw eu rhestr dysgwyr yn barod eto. Rhowch gynnig arall arni mewn munud.',

  refresh: 'Adnewyddu',
  reloadMicroreels: 'Ail-lwytho’r microreels',
  generationStopped: (ready: number, count: number) =>
    `Daeth y cynhyrchu i ben ar ôl ${ready} o ${count} microreel.`,
  stoppedBeforeFinishing: (count: number) =>
    count === 1
      ? 'Daeth y cynhyrchu i ben cyn i’ch microreel gael ei orffen.'
      : 'Daeth y cynhyrchu i ben cyn i’ch microreels gael eu gorffen.',
  tryAgain: 'Rhoi cynnig arall arni',

  phoneLabel: (title: string, number: number, count: number) =>
    `${title} · microreel ${number}/${count}`,
  watchPlayablePreview: 'Gwylio’r rhagolwg chwaraeadwy',
  watchFinishedVideo: 'Gwylio’r fideo gorffenedig',
  previewReady: 'Mae’r rhagolwg yn barod i’w chwarae.',
  loadingPreview: 'Yn llwytho’r rhagolwg…',
  previewFrameTitle: (title: string, reelTitle: string) =>
    `${title}: rhagolwg chwaraeadwy ${reelTitle}`,
  previewSlow: 'Ydy’r rhagolwg yn cymryd gormod o amser?',
  openPreviewInTab: 'Agor y rhagolwg chwaraeadwy mewn tab newydd',
  learnerPreview: 'Rhagolwg y dysgwr',
  exitPreview: 'Gadael y rhagolwg',
  sceneMarker: (number: number) => `Golygfa ${number}`,
  clipMarker: (number: number) => `Clip ${number}`,

  makingMicroreels: 'Yn creu eich microreels',
  renderingScenes:
    'Yn creu’r naratif a’r delweddau. Bydd pob un yn chwarae yma pan fydd yn barod.',
  renderingClips:
    'Yn torri’r clipiau o’ch fideo. Bydd pob un yn chwarae yma pan fydd yn barod.',
  renderSteps: [
    'Paratoi eich rhagolwg',
    'Allforio’r microreels',
    'Gorffen eich ffeiliau',
  ],
  clipRenderSteps: [
    'Torri’r clipiau o’ch fideo',
    'Allforio’r microreels',
    'Gorffen eich ffeiliau',
  ],
  elapsed: (seconds: string) => `${seconds}s`,
  watchMicroreel: (number: number) => `Gwylio microreel ${number}`,
  notGenerated: (count: number) =>
    count === 1
      ? 'Nid oedd modd cynhyrchu’r microreel'
      : 'Nid oedd modd cynhyrchu’r microreels',
  reelNotGenerated: 'Nid oedd modd cynhyrchu’r microreel hwn',
  reelNotGeneratedYet: 'Nid yw’r microreel hwn wedi’i gynhyrchu eto',
  stillDrafting: 'Mae eich bwrdd stori yn dal i gael ei ddrafftio',
  openStoryboard: 'Agor y bwrdd stori',
  storyboardChanged: 'Mae’r bwrdd stori wedi newid',
  newLookReady: 'Mae golwg newydd yn barod i’w gweld',
  newLookHint:
    'Ail-gynhyrchwch i’w gweld. Mae’r naratif, y llais a’r lluniau’n cael eu hailddefnyddio.',
  regenerateScenes:
    'Ail-gynhyrchwch i weld eich newidiadau. Caiff golygfeydd heb eu newid eu hailddefnyddio.',
  regenerateClips:
    'Ail-gynhyrchwch i weld eich newidiadau. Caiff clipiau heb eu newid eu hailddefnyddio.',
  regenerate: 'Ail-gynhyrchu',
  noneYet: (count: number) =>
    count === 1 ? 'Dim microreel eto' : 'Dim microreels eto',
  reviewThenGenerate:
    'Adolygwch y bwrdd stori, yna cynhyrchwch. Nid yw’r microreel hwn wedi’i gynhyrchu eto.',
  reviewStoryboard: 'Adolygu’r bwrdd stori',

  listLabel: 'Microreels',
  changedAfterMade:
    'Newidiodd y bwrdd stori ar ôl i’r microreels hyn gael eu gwneud.',
  selectToPlay: 'Dewiswch un i’w chwarae',
  chipPreviewReady: 'Rhagolwg yn barod',
  chipUpNext: 'Nesaf',
  chipGenerating: 'Yn cynhyrchu…',
  chipNotGenerated: 'Heb ei gynhyrchu',
  chipChanged: 'Wedi newid ers y cynhyrchu diwethaf',
  scenes: (count: number) => `${count} golygfa`,
  clips: (count: number) => `${count} clip`,
  editStoryboard: 'Golygu’r bwrdd stori',
  editStoryboardOf: (title: string) => `Golygu bwrdd stori ${title}`,
  editKnowledgeCheck: 'Golygu’r gwiriad gwybodaeth',
  editKnowledgeCheckOf: (title: string) => `Golygu gwiriad gwybodaeth ${title}`,
  editAttestation: 'Golygu’r cadarnhad polisi',
  editAttestationOf: (title: string) => `Golygu cadarnhad polisi ${title}`,
  downloadVideo: 'Llwytho’r fideo i lawr',
  downloadReel: (title: string) => `Llwytho ${title} i lawr`,

  brandingSummary: 'Gosodiadau fideo a brandio · pob microreel',
  lookSummary: (title: string) => `Golwg a sain · ${title}`,
  sourceReferences: 'Cyfeiriadau at y ffynhonnell',
};

export const reviewText: typeof english = getLocalizationBasedOnLang(
  english,
  welsh,
);
